import React from "react";
import { OptimizedGantt } from "@planzone/dto";
import { DateTime } from "luxon";
import { Context } from "@scenes/workloads/context";
import { getDate } from "../utils/getDate";
import { isDone, isEvent, isMilestone, isTask } from "../utils";
import { Props } from "./types";
import styles from "./styles.scss";

function getEndDate(
  element: OptimizedGantt[number],
  userTimezone: string,
): DateTime {
  const completionDate = getDate(element.completionDate, userTimezone);

  return isDone(element) &&
    !!element.endDate &&
    !!element.startDate &&
    isTask(element)
    ? completionDate
    : getDate(element.endDate, userTimezone);
}

function getOvertime(
  element: OptimizedGantt[number],
  limitDate: DateTime,
  userTimezone: string,
): number {
  // Bugfix preserved from Phase 1 commit `fb6bae6e7`: previously the
  // `limitDate.toString()` round-trip ran through a locale-dependent string
  // format. Now operates directly on Luxon DateTime values throughout.
  // Workloads' Overtime omits Gantt's `recalculatedEndDate` arg — workloads
  // has no `useElementContext`/state.minDate chain to recompute from, so the
  // 2-arg signature is the canonical workloads shape.
  const end = getEndDate(element, userTimezone);
  const today = DateTime.now().setZone(userTimezone).startOf("day");
  const lDate = limitDate.startOf("day");
  const adjustedEnd = end > lDate ? lDate : end;
  return today.diff(adjustedEnd, "days").days;
}

function Overtime({ element, isDisabled, limitDate, children }: Props) {
  const userTimezone = DateTime.local().zoneName;
  const { cell, areDelaysToggled } = React.useContext(Context);
  const overtime = getOvertime(element, limitDate, userTimezone);
  const canPrintOvertime =
    !isMilestone(element) && !isEvent(element) && !isDone(element);
  const hasOvertime = overtime > 0;

  return (
    <div className={styles.overtime}>
      {children(overtime)}
      {canPrintOvertime && hasOvertime && (
        <div
          className={styles.indicator}
          style={{
            width: isDisabled || !areDelaysToggled ? 0 : cell.width * overtime,
          }}
        />
      )}
    </div>
  );
}

export { Overtime };
